import type { ComponentPropsWithRef, MouseEvent, ReactNode } from "react";
import styles from "./Button.module.css";
import { cx } from "./cx.js";

interface ButtonProps extends ComponentPropsWithRef<"button"> {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "base" | "lg";
  /** Shows a spinner and swallows clicks, without collapsing the label. */
  loading?: boolean;
  fullWidth?: boolean;
  children?: ReactNode;
}

export function Button({
  variant = "secondary",
  size = "base",
  loading = false,
  fullWidth = false,
  disabled,
  type = "button",
  className,
  children,
  onClick,
  ...rest
}: ButtonProps) {
  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    if (loading) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  }

  return (
    <button
      type={type}
      disabled={disabled}
      // Busy rather than disabled: a disabled button loses focus mid-submit and
      // the state change goes unannounced.
      aria-disabled={loading || undefined}
      aria-busy={loading || undefined}
      onClick={handleClick}
      className={cx(
        styles.button,
        styles[variant],
        size !== "base" && styles[`size-${size}`],
        fullWidth && styles.full,
        className,
      )}
      {...rest}
    >
      {loading && <span className={styles.spinner} aria-hidden="true" />}
      {children}
    </button>
  );
}
